import { DateTime } from 'luxon'
import { MLPBatches, MLPCase } from '../MLPContext'
import { MLPResult } from '../MLPStore'

export const getItemState = (results: MLPResult[][], itemIndex: number, batchIndex: number) => {
    const batch = results[batchIndex]
    if (batch) {
        const item = batch[itemIndex]
        if (item) {
            return item.category
        }
    }
    return false
}
export const findIfAllCasesInBatchAreFilled = (mlpBatches: MLPBatches, results: MLPResult[][], batchIndex: number) => {
    //check if all items in results are filled
    const batch = mlpBatches[batchIndex]
    if (batch) {
        for (let i = 0; i < batch.length; i++) {
            if (!results[batchIndex]?.[i]) {
                return false
            }
        }
    }
    if (mlpBatches[batchIndex]?.length === results[batchIndex]?.length) return true
    return false
}

export interface MlpLotIntermediateBatch {
    // violator: any
    lot: string
    oldestTimestamp?: string
    cases: MLPCase[]
}

export function sortAndGroupMLPData(data: MLPCase[]): MlpLotIntermediateBatch[] {
    //group by lot into a map , by parking_lot
    const lotMap = new Map<string, Array<MLPCase>>()
    data.forEach((item) => {
        const existingLots = lotMap.get(item.offender.parking_lot) || []
        lotMap.set(item.offender.parking_lot, [...existingLots, item])
    })
    //batch by lot and get olderst timestamp
    const mlpLotBatches: MlpLotIntermediateBatch[] = [...lotMap.entries()].map(([lot, cases]: [string, MLPCase[]]) => {
        const sortedCases = cases.sort((a, b) => {
            const aTimestamp = new Date(a.offender.timestamp_in).getTime()
            const bTimestamp = new Date(b.offender.timestamp_in).getTime()
            return aTimestamp - bTimestamp
        })
        return {
            lot,
            oldestTimestamp: sortedCases[0].offender.timestamp_in,
            cases: sortedCases
        }
    })

    //order by oldest timestamp for each lot
    const batchesOrderedByOldestTimestamp = mlpLotBatches.sort((a, b) => {
        const aTimestamp = new Date(a.oldestTimestamp as string).getTime()
        const bTimestamp = new Date(b.oldestTimestamp as string).getTime()
        return aTimestamp - bTimestamp
    })
    return batchesOrderedByOldestTimestamp
}

export const splitIfCasesInLotMoreThanBatchSize = function* (
    finalBatches: MLPBatches,
    BATCH_SIZE: number,
    lotCases: MlpLotIntermediateBatch,
    currentIndex: number
): Generator<MLPBatches | number> {
    const remainingCases = lotCases.cases.slice() // Copy the original array
    while (remainingCases.length > 0) {
        // Get the next batch of cases
        const numberOfCasesInCurrentBatch = finalBatches[currentIndex]?.length || 0
        const newCasesToInsert = remainingCases.splice(0, BATCH_SIZE - numberOfCasesInCurrentBatch)
        // Update currentIndex and add the batch to finalBatches
        finalBatches[currentIndex] = [...(finalBatches[currentIndex] || []), ...newCasesToInsert]
        // If the batch size is reached, move to the next index
        if (finalBatches[currentIndex].length >= BATCH_SIZE) {
            currentIndex++
        }
    }
    yield finalBatches
    yield currentIndex
}

export function batchMlPData(sortedData: MlpLotIntermediateBatch[]): MLPBatches {
    let finalBatches: MLPBatches = [[]] //matrix of casesx batch
    const BATCH_SIZE = 20
    let currentIndex = 0
    sortedData.forEach((lotCases) => {
        if (lotCases.cases.length > BATCH_SIZE) {
            const largeCasesIterator = splitIfCasesInLotMoreThanBatchSize(
                finalBatches,
                BATCH_SIZE,
                lotCases,
                currentIndex
            )
            finalBatches = [...largeCasesIterator.next().value]
            currentIndex = largeCasesIterator.next().value
        } else {
            //normal scenario
            if (lotCases.cases.length + finalBatches[currentIndex].length <= BATCH_SIZE) {
                finalBatches[currentIndex] = [...finalBatches[currentIndex], ...lotCases.cases]
            } else {
                currentIndex += 1
                finalBatches[currentIndex] = [...lotCases.cases]
            }
        }
    })
    return finalBatches
}
const attachFakeImages = (data: MLPCase[]) => {
    const getRandomLpImage = () => {
        return 'https://via.placeholder.com/150x50?text=LP+Image+Not+Available'
    }
    const getRandomDetectionImage = () => {
        return 'https://via.placeholder.com/640x360?text=Detection+Image+Not+Available'
    }
    return data.map((item) => {
        return {
            ...item,
            offender: {
                ...item.offender,
                signed_url_in_lp: item?.offender?.signed_url_in_lp || getRandomLpImage(),
                signed_url_out_lp: item?.offender?.signed_url_out_lp || getRandomLpImage(),
                signed_url_in_render: item?.offender?.signed_url_in_render || getRandomDetectionImage(),
                signed_url_out_render: item?.offender?.signed_url_out_render || getRandomDetectionImage()
            },
            similar_detections: [...item?.similar_detections]
                ?.sort((a, b) => {
                    const dateA = DateTime.fromISO(a?.timestamp)
                    const dateB = DateTime.fromISO(b?.timestamp)
                    return dateA?.valueOf() - dateB?.valueOf()
                })
                .map((similarDetection) => {
                    return {
                        ...similarDetection,
                        signed_url_render: similarDetection?.signed_url_render || getRandomDetectionImage(),
                        signed_url_lp: similarDetection?.signed_url_lp || getRandomLpImage()
                    }
                })
        }
    })
}
export const processMLPData = (data: MLPCase[]): MLPBatches => {
    const sortedData = sortAndGroupMLPData(attachFakeImages(data))
    const batchedMlpCases = batchMlPData(sortedData)
    return batchedMlpCases
}

// const lpImages = [
//     'https://i.imgur.com/vhG5hia.jpeg',
//     'https://i.imgur.com/9Soknlc.jpg',
//     'https://i.imgur.com/Oxvz64f.jpg',
//     'https://i.imgur.com/U7DXPJi.jpg',
//     'https://i.imgur.com/DBMz0L8.jpg',
//     'https://i.imgur.com/sWO7UCI.jpg',
//     'https://i.imgur.com/mGC7dTq.jpg',
//     'https://i.imgur.com/JO4he2T.jpg',
//     'https://i.imgur.com/3TX9hTN.jpg',
//     'https://i.imgur.com/C0FMxuF.jpg',
//     'https://i.imgur.com/JlPTCbF.jpg',
//     'https://i.imgur.com/6a8p9tE.jpg',
//     'https://i.imgur.com/vFgfu5s.jpg',
//     'https://i.imgur.com/ZVXtkXW.jpg',
//     'https://i.imgur.com/0D3khXf.jpg',
//     'https://i.imgur.com/rLckL1O.jpg',
//     'https://i.imgur.com/NOJWt12.jpg',
//     'https://i.imgur.com/Kowo8c2.jpg'
// ]
